import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import {
    TCI_UPDATE_ACTION_TYPE,
    ITCIFileMap,
    ITCIFile,
    ITCIApplicationUpdateProvider,
    ITCIApplicationUpdateRequest,
    ITCIValidationResult,
    TCITenantValidationResult,
    TCITenantAppType,
    TCITenantSelectorType,
    TCIActionState
} from "@tci/common";
import { TciTenantService } from "../../ng-tenant/tci-tenant.service";
import { TciTenantModelService } from "../../ng-tenant/tci-tenant.model";
import {
    BaseAppDetailMenuComponentInput,
    BaseAppDetailMenuComponentOutput
} from "../../ng-app-details/ng-app-details-menu";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCIAppDetailsMenuOutput, ITCIUploadSupportedFile, ITCIUploadFileCheckResult } from "../../ng-tenant/tci-tenant-components";

import { UploadEvent } from "ngx-file-drop";
import { Observable } from "rxjs";
import * as lodash from "lodash";

const ENCODING_STR = "Binary";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_UPLOAD_EXISTING_APP.toString(),
    templateUrl: "./base-app-hm-upload-existing.component.html",
    styleUrls: ["./base-app-hm-upload-existing.component.less"]
})
export abstract class BaseAppDetailUploadExistingComponent<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCIAppDetailsMenuOutput>>
    extends AbstractTCITenantComponent<E, I, EventEmitter<ITCIAppDetailsMenuOutput>> implements OnInit {
    @Output() output: EventEmitter<ITCIAppDetailsMenuOutput> = new EventEmitter();
    @ViewChild("inputFiles") FilesInput: ElementRef;

    private allowedFiles: ITCIUploadSupportedFile[] = [];
    private files: ITCIFileMap = {};
    private fileNames = {};
    private droppedFiles: { [key: string]: ITCIUploadFileCheckResult } = {};
    private errorMsg: string;

    working = false;
    requestInput: ITCIApplicationUpdateRequest;
    allowToPublish = false;

    static ReadFile(fileEntry: File): Observable<ITCIFile> {
        return Observable.create(observer => {
            const reader = new FileReader();
            reader.onloadend = f => {
                const res: ITCIFile = {
                    content: <string>reader.result,
                    mimeType: fileEntry.type,
                    encoding: ENCODING_STR
                };
                observer.next(res);
                observer.complete();
            };
            reader.onerror = f => {
                const msg = "File is not readable";
                console.error(msg, f);
                observer.throw(new Error(msg));
                observer.complete();
            };
            reader.readAsBinaryString(fileEntry);
        });
    }

    public abstract getFileSpecs(): ITCIUploadSupportedFile[];
    public abstract getTenantType(): TCITenantAppType;

    constructor(
        public tenantSvc: TciTenantService,
        public tenantModelSvc: TciTenantModelService,
        public toastrService: NgToastrService
    ) {
        super();
    }

    ngOnInit() {
        this.allowedFiles = this.getFileSpecs();
        this.initFilesInput();

        this.requestInput = {
            tenantType: this.getTenantType(),
            actionType: TCI_UPDATE_ACTION_TYPE.UPLOAD_EXISTING,
            appId: this.input.application.id,
            application: this.input.application
        };
    }

    protected getProvider(): Observable<ITCIApplicationUpdateProvider> {
        return this.tenantSvc.onInit()
            .map(() => this.tenantModelSvc.getTenant(this.getTenantType()))
            .map(tenant => tenant.getAppUpdateProvider())
            .catch(err => {
                const errLabel = "Error getting UpdateProvider";
                console.warn(errLabel, err);
                return Observable.throw(errLabel);
            });
    }

    // For those files selected via dialog
    protected initFilesInput() {
        if (this.FilesInput && this.FilesInput.nativeElement) {
            this.FilesInput.nativeElement
                .addEventListener("change", (e: any) => this.handleSelectedFiles(<File[]>lodash.toArray(e.target.files)));
        }
    }

    // For dropped files
    protected dropped(event: UploadEvent) {
        const files: File[] = [];
        let processed = 0;
        event.files
            .filter(f => f.fileEntry.isFile)
            .forEach(f => {
                f.fileEntry.file((fe: File) => {
                    files.push(fe);

                    processed++;
                    if (processed === event.files.length) {
                        this.handleSelectedFiles(files);
                    }
                });
            });
    }

    protected handleSelectedFiles(files: File[]) {
        this.errorMsgHide();
        const emitErr = (msg: string) => {
            this.errorMsg = msg;
            if (lodash.size(this.droppedFiles) < this.allowedFiles.length) { // There are already valid files
                this.allowToPublish = false;
            }
        };

        if (this.allowedFiles.length < files.length) {
            emitErr("There are more selected files than expected");
        } else if (
            this.allowedFiles.length > files.length &&
            !this.allowedFiles.some(a => files.map(f => f.name).some(f => f.endsWith("." + a.extension)))
        ) {
            emitErr("There are unsupported files in your selection");
        } else if (
            this.allowedFiles.length === files.length &&
            !this.allowedFiles.every(a => files.map(f => f.name).some(f => f.endsWith("." + a.extension)))
        ) {
            emitErr("There are unsupported files in your selection");
        } else if (lodash.some(lodash.countBy(files, file => lodash.last(file.name.split("."))), count => count > 1)) {
            emitErr(`Selected files have more than one ${this.allowedFiles.map(f => f.extension).join(" or ")}`);
        } else {
            const fileCheck: Observable<ITCIUploadFileCheckResult>[] = files.map(file => Observable.create(observer => {
                const name: string = file.name;
                const res: ITCIUploadFileCheckResult = {
                    type: lodash.first(this.allowedFiles.filter(f => name.endsWith("." + f.extension))).extension,
                    fileContent: null, name, error: false, errorMsg: null
                };

                if (!this.files[res.type]) { this.files[res.type] = <any>{}; }
                this.files[res.type].content = "";

                BaseAppDetailUploadExistingComponent.ReadFile(file)
                    .finally(() => {
                        observer.next(res);
                        observer.complete();
                    })
                    .subscribe(
                        fileContent => res.fileContent = fileContent,
                        () => {
                            res.error = true;
                            res.errorMsg = "Can't read one or more selected files";
                        }
                    );
            }));

            Observable.merge(...fileCheck)
                .toArray()
                .subscribe(DroppedFiles => {
                    if (!DroppedFiles.some(fcr => fcr.error)) {
                        DroppedFiles.forEach(fr => {
                            this.files[fr.type] = fr.fileContent;
                            this.fileNames[fr.type] = fr.name;
                            this.droppedFiles[fr.type] = fr; // Feed the dropped files array
                        });
                        this.processFiles();
                    } else {
                        this.errorMsg = lodash.sample(DroppedFiles).errorMsg;
                        this.allowToPublish = false;
                    }
                });
        }
    }

    protected removeFile(file: ITCIUploadSupportedFile) {
        this.errorMsgHide();
        this.FilesInput.nativeElement.value = "";
        this.files[file.extension] = { content: null, encoding: ENCODING_STR, mimeType: null };
        this.fileNames[file.extension] = "";
        delete this.droppedFiles[file.extension];
        this.allowToPublish = false;
    }

    protected isFileValid(file: ITCIUploadSupportedFile) {
        return this.files[file.extension]
            && !lodash.isNull(this.files[file.extension].content)
            && !lodash.isEmpty(this.files[file.extension].content)
            && !lodash.isEmpty(this.files[file.extension].encoding);
    }

    protected errorMsgHide() {
        this.errorMsg = undefined;
    }

    protected setErrorMsg(msg: string) {
        this.errorMsg = msg;
    }

    onCancel(): void {
        this.closeModal();
    }

    protected closeModal(refresh = false): ITCIAppDetailsMenuOutput {
        const e: ITCIAppDetailsMenuOutput = {
            menuItemSelector: this.input.componentSelector,
            closeModal: true,
            refresh
        };
        this.output.emit(e);
        return e;
    }

    // <Second part>
    protected processFiles() {
        this.allowToPublish = false;
        const files = lodash.toArray(this.droppedFiles);
        this.errorMsgHide();

        // File attachment
        this.requestInput.appData = {};
        files.forEach(file => this.requestInput.appData[file.name] = file.fileContent);

        this.getProvider().subscribe(provider => {
            provider.validate(this.requestInput).subscribe(iTenantValidationResult => {
                const tenantValidationResult = new TCITenantValidationResult(iTenantValidationResult);
                if (tenantValidationResult.isOK()) {
                    this.allowToPublish = true;
                } else if (tenantValidationResult.isError()) {
                    this.setErrorMsg(lodash.get(lodash.sample(tenantValidationResult.getErrors()), "errorMsg", "Unexpected error"));
                }
            });
        });
    }

    protected replaceApp() {
        this.working = true;

        const errToastr = (res?: ITCIValidationResult, msg?: string) => {
            if (res && res.getErrors) {
                msg = res.getErrors().map(err => err.getErrorMessage()).pop();
            } else if (!lodash.isString(msg)) {
                msg = "Unexpected error";
            }

            this.toastrService.error(`The application ${this.input.application.name} was not updated`, msg);
            this.working = false;
        };

        this.getProvider().subscribe(provider => {
            provider.updateApplication(this.requestInput)
                .do(() => this.working = false)
                .subscribe(
                    updRes => {
                        if (updRes.success) {
                            this.toastrService.success(this.input.application.name, "The application has been updated");
                            // this.output.emit({ closeModal: true, refresh: true });
                            this.closeModal(true);
                            // alert("What to do next?\nRefresh the view?\nNavigate to the application details?");
                        } else {
                            errToastr(null, updRes.msg);
                        }
                    },
                    err => errToastr(err)
                );
        });
    }
    // <Second part>
}
